// src/contexts/SelectedStateContext.js
//
// Single authoritative source of truth for the user's selected US state.
// Initialized once from localStorage and kept in React state so that all
// consumers re-render reactively — including when a mobile `focus` event fires
// after the app is restored from background and localStorage becomes populated.
//
// State resolution priority (highest to lowest):
//   1. Explicit URL state segment (e.g. /oh, /value-finder/oh)
//   2. Saved user state (previously stored in localStorage, either picked
//      manually or auto-detected on an earlier visit)
//   3. Detected location (one-time, best-effort geolocation lookup — only
//      attempted when neither 1 nor 2 apply)
//   4. DEFAULT_STATE fallback (the existing state-selection system: manual
//      Navbar dropdown / PreviewGate remain fully functional at all times)
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { urlHasExplicitState } from '../utils/stateRouting';
import { detectStateFromLocation, hasGeoDetectionBeenAttempted } from '../utils/geoLocation';

export const DEFAULT_STATE = 'FL';

const SelectedStateContext = createContext(null);

export function SelectedStateProvider({ children }) {
  const [selectedState, setSelectedState] = useState(
    () => localStorage.getItem('selectedState') || DEFAULT_STATE
  );
  const [autoDetectedState, setAutoDetectedState] = useState(false);

  // True once any explicit selection has been made/observed during this page
  // load (a saved value already existed at mount, the URL carries a state
  // segment, or the user/URL-sync has called updateState). Guards the
  // async geolocation result so it can never clobber a higher-priority
  // selection that happened first or arrived while location was resolving.
  const explicitStateSetRef = useRef(
    !!localStorage.getItem('selectedState') || urlHasExplicitState(window.location.pathname)
  );

  // Write to localStorage and update React state atomically.
  const updateState = useCallback((code) => {
    explicitStateSetRef.current = true;
    localStorage.setItem('selectedState', code);
    setSelectedState(code);
  }, []);

  // Keep in sync with:
  //  • changes made in other tabs/windows (`storage` event)
  //  • localStorage becoming available after the mobile app is restored from
  //    background / PWA cold-start (`focus` event)
  useEffect(() => {
    const sync = () => {
      const saved = localStorage.getItem('selectedState');
      if (saved) setSelectedState(saved);
    };
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  // One-time, best-effort location-based state detection. Runs entirely in
  // the background after mount — it never delays the initial render, and it
  // is skipped outright whenever a higher-priority state source (URL or a
  // previously saved selection) is already present, or if we've already
  // asked the user for permission before (so we never re-prompt).
  useEffect(() => {
    if (explicitStateSetRef.current || hasGeoDetectionBeenAttempted()) {
      return;
    }

    let cancelled = false;
    detectStateFromLocation().then((code) => {
      if (cancelled || !code) return;
      // Re-check at resolution time: the user may have picked a state (or
      // the URL sync may have fired) while we were waiting on the browser's
      // permission prompt / GPS fix.
      if (explicitStateSetRef.current) return;
      updateState(code);
      setAutoDetectedState(true);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SelectedStateContext.Provider
      value={{ selectedState, updateState, autoDetectedState }}
    >
      {children}
    </SelectedStateContext.Provider>
  );
}

export function useSelectedState() {
  const ctx = useContext(SelectedStateContext);
  if (!ctx) throw new Error('useSelectedState must be used within a SelectedStateProvider');
  return ctx;
}
